// NavBar.js

import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Flex,
  Tooltip,
  Box,
  HStack,
  VStack,
  Text,
  Input,
  Spinner,
  Button,
  Badge,
  Icon,
  IconButton,
  Image,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
} from "@chakra-ui/react";
import { ChevronDownIcon, EditIcon } from "@chakra-ui/icons";
import {
  PanelLeftOpen,
  PanelLeftClose,
  Folder,
  FolderOpen,
} from "lucide-react";
import FloatingChatbot from "./FloatingChatbot";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "./UserContext";
import UserMenu from "./UserMenu";
import SOWModal from "./SOWModal";
import JobsDrawer from "./JobsDrawer";
import { usePlanLimits } from "../hooks/usePlanLimits";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { clientLabel } from "./client/clientTools";

// Which AI tools already work with this plan, read when the menu opens.
function ConnectedToolsSummary({ planId }) {
  const [names, setNames] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchWithAuth(`/api/client-setup/plans/${planId}/connections`)
      .then((response) => (response.ok ? response.json() : { connections: [] }))
      .then((value) => { if (!cancelled) setNames([...new Set((value.connections || []).map((item) => clientLabel(item.client_name)))]); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [planId]);
  if (!names?.length) return null;
  return (
    <Text fontSize="xs" color="gray.500" px={3} pb={1} mt={-0.5}>
      Connected to {names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0]}
    </Text>
  );
}

function NavBar({
  planName,
  currentDesignChallenge,
  selectedPrograms,
  shortSummary,
  goalStatement,
  problemStatement,
  handleComponentAction,
  targetStatement,
  objectivesStatement,
  deliverablesStatement,
  metricsStatement,
  stakeholdersCustomersInvolvement,
  finalProgramSequence,
  totalDuration,
  prefilledPrompt,
  triggeredByButton,
  isSaved = true,
  isSaving = false,
  planState = "idle",
  currentPlanId,
  onOpenShareModal,
  onOpenMcpModal,
  onOpenLearningsPanel,
  learningsPendingCount = 0,
  onRenamePlan,
  strategyLifecycleStatus = "exploring",
  onStrategyLifecycleStatusChange,
}) {
  const {
    chatbotRef,
    loading,
    userDetails,
    isChatCollapsed,
    setIsChatCollapsed,
    isProjectsDrawerOpen,
    openProjectsDrawer,
  } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isHoveringTitle, setIsHoveringTitle] = useState(false);
  const nameInputRef = useRef(null);
  const navRef = useRef(null);
  const [navWidth, setNavWidth] = useState(0);
  const {
    planStatus,
    refreshPlanStatus,
  } = usePlanLimits();

  // Initialize state for SOW modal
  const [isSOWModalOpen, setIsSOWModalOpen] = useState(false);
  const [isJobsOpen, setIsJobsOpen] = useState(false);
  const [jobsCount, setJobsCount] = useState(0);

  useEffect(() => {
    if (!userDetails) return;
    const fetchJobsCount = async () => {
      try {
        const planIdFromUrl = location.pathname.match(/\/plan\/(\d+)/)?.[1];
        const url = planIdFromUrl
          ? `/api/agent/tasks/user/count?planId=${planIdFromUrl}`
          : '/api/agent/tasks/user/count';
        const res = await fetchWithAuth(url);
        if (res?.ok) {
          const data = await res.json();
          setJobsCount(data.count || 0);
        }
      } catch { /* non-critical */ }
    };
    fetchJobsCount();
    const interval = setInterval(fetchJobsCount, 30000);
    return () => clearInterval(interval);
  }, [userDetails, location.pathname]);

  const [jobsDefaultTab, setJobsDefaultTab] = useState('Tasks');
  const [jobsInitialTaskId, setJobsInitialTaskId] = useState(null);

  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement || typeof ResizeObserver === "undefined") return undefined;

    const updateWidth = () => setNavWidth(navElement.getBoundingClientRect().width);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(navElement);
    return () => observer.disconnect();
  }, []);

  const useCompactPlanActions = navWidth > 0 && navWidth < 1150;

  useEffect(() => {
    const handleOpenJobs = (e) => {
      setJobsDefaultTab(e.detail?.tab || 'Tasks');
      setJobsInitialTaskId(e.detail?.taskId || null);
      setIsJobsOpen(true);
    };
    const handleOpenWorkflows = () => { setJobsDefaultTab('Workflows'); setIsJobsOpen(true); };
    const handleCloseJobs = () => { setIsJobsOpen(false); setJobsInitialTaskId(null); };
    window.addEventListener('pabs:openJobs', handleOpenJobs);
    window.addEventListener('pabs:openWorkflows', handleOpenWorkflows);
    window.addEventListener('pabs:closeJobs', handleCloseJobs);
    return () => {
      window.removeEventListener('pabs:openJobs', handleOpenJobs);
      window.removeEventListener('pabs:openWorkflows', handleOpenWorkflows);
      window.removeEventListener('pabs:closeJobs', handleCloseJobs);
    };
  }, []);

  // State for status fade-out behavior
  const [statusVisible, setStatusVisible] = useState(true);
  const [shouldShowStatus, setShouldShowStatus] = useState(true);
  const fadeTimeoutRef = useRef(null);

  const handleCloseSOWModal = () => {
    setIsSOWModalOpen(false);
  };

  // Handle fade-out behavior for "Saved" status
  useEffect(() => {
    // Clear any existing timeout
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
    }

    // If currently saving or in other active states, show status immediately
    if (
      planState === "saving" ||
      planState === "loading" ||
      planState === "updating" ||
      planState === "creating"
    ) {
      setStatusVisible(true);
      setShouldShowStatus(true);
      return;
    }

    // If saved, show for 2.5 seconds then fade out
    if (planState === "saved") {
      setStatusVisible(true);
      setShouldShowStatus(true);

      fadeTimeoutRef.current = setTimeout(() => {
        setStatusVisible(false);
        // After fade animation, hide completely
        setTimeout(() => {
          setShouldShowStatus(false);
        }, 300); // Match CSS transition duration
      }, 2500); // Show for 2.5 seconds
    }

    // Cleanup timeout on unmount
    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, [planState]);

  // Check if loading is true, show a spinner until the data is ready
  if (loading) {
    return <Spinner size="lg" />;
  }

  // Check if we're on a plan page (either /plan or /plan/:id)
  const isOnPlanPage =
    location.pathname === "/plan" || location.pathname.startsWith("/plan/");
  const hasWorkspaceControls =
    isOnPlanPage ||
    location.pathname === "/brief" ||
    location.pathname.startsWith("/c/");
  const hasProjectsControl =
    hasWorkspaceControls ||
    location.pathname === "/profile" ||
    location.pathname === "/account" ||
    location.pathname === "/plan-usage" ||
    location.pathname === "/billing" ||
    location.pathname === "/data-privacy" ||
    location.pathname === "/connectors" ||
    location.pathname.startsWith("/connectors/") ||
    location.pathname === "/brand";

  // Check if we're on Brief or Settings pages where we don't want hamburger menu
  const isBriefOrSettingsPage =
    location.pathname === "/brief" ||
    location.pathname === "/profile" ||
    location.pathname === "/account" ||
    location.pathname === "/plan-usage" ||
    location.pathname === "/data-privacy" ||
    location.pathname === "/integrations" ||
    location.pathname === "/connectors" ||
    location.pathname === "/brand";

  // Extract program title based on location and summary
  const programTitle = planName || shortSummary || "New Plan";
  const strategyStatusOptions = [
    {
      value: "exploring",
      label: "Exploring",
      description: "You are still using this strategy to think through the idea.",
      color: "blue.400",
      bg: "blue.50",
    },
    {
      value: "alive",
      label: "Alive",
      description: "This strategy is still current, relevant, and worth keeping active.",
      color: "green.400",
      bg: "green.50",
    },
    {
      value: "parked",
      label: "Parked",
      description: "The idea may still be valid, but it is not a current priority.",
      color: "orange.400",
      bg: "orange.50",
    },
    {
      value: "got_what_i_needed",
      label: "Got what I needed",
      description: "PABS helped clarify the idea and you do not need ongoing nudges.",
      color: "teal.400",
      bg: "teal.50",
    },
    {
      value: "not_pursuing",
      label: "Not pursuing",
      description: "You have decided not to continue with this idea.",
      color: "gray.400",
      bg: "gray.50",
    },
  ];
  const currentStrategyStatus =
    strategyStatusOptions.find(option => option.value === strategyLifecycleStatus) ||
    strategyStatusOptions[0];

  // Render left-side elements based on the page
  const renderLeftSide = () => {
    return (
      <Flex
        direction="row"
        align="center"
        p={2}
        pl={{ base: 3, md: 4 }}
        pr={4}
        mt={0}
        pointerEvents="auto"
        flex="1"
        minWidth="0"
        gap={3}
      >
        {(hasWorkspaceControls || hasProjectsControl) && (
          <HStack spacing={1} flexShrink={0}>
            {hasWorkspaceControls && (
              <Tooltip
                label={isChatCollapsed ? "Show chat panel" : "Hide chat panel"}
                hasArrow
                placement="bottom"
              >
                <IconButton
                  aria-label={isChatCollapsed ? "Show chat panel" : "Hide chat panel"}
                  icon={
                    isChatCollapsed
                      ? <PanelLeftOpen size={20} strokeWidth={1.8} />
                      : <PanelLeftClose size={20} strokeWidth={1.8} />
                  }
                  size="sm"
                  variant="ghost"
                  borderRadius="lg"
                  bg={isChatCollapsed ? "transparent" : "gray.100"}
                  color="gray.500"
                  _hover={{ bg: "gray.100", color: "gray.700" }}
                  onClick={() => setIsChatCollapsed((collapsed) => !collapsed)}
                />
              </Tooltip>
            )}
            {hasProjectsControl && (
              <Tooltip label="Projects and conversations" hasArrow placement="bottom">
                <IconButton
                  aria-label="Open projects and conversations"
                  icon={
                    isProjectsDrawerOpen
                      ? <FolderOpen size={20} strokeWidth={1.8} />
                      : <Folder size={20} strokeWidth={1.8} />
                  }
                  size="sm"
                  variant="ghost"
                  borderRadius="lg"
                  color="gray.500"
                  bg={isProjectsDrawerOpen ? "gray.100" : "transparent"}
                  _hover={{ bg: "gray.100", color: "gray.700" }}
                  onClick={(event) => {
                    const rect = event.currentTarget.getBoundingClientRect();
                    openProjectsDrawer({
                      x: rect.left,
                      y: rect.bottom,
                      width: rect.width,
                    });
                  }}
                />
              </Tooltip>
            )}
          </HStack>
        )}
        {/* Logo — shown on all pages */}
        <Tooltip label="Home" hasArrow placement="bottom">
          <Image
            src="/images/pablo_logo1.0.svg"
            alt="PABS"
            boxSize="28px"
            cursor="pointer"
            flexShrink={0}
            onClick={() => navigate('/brief')}
          />
        </Tooltip>
        {isOnPlanPage && (
          <HStack spacing={2} align="center" minWidth="0" flex="1">
            <HStack align="center" spacing={2} maxWidth="430px" flexShrink={1} minWidth="0">
              {isEditingName ? (
                <Input
                  ref={nameInputRef}
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={() => {
                    const trimmed = editedName.trim();
                    if (trimmed && trimmed !== programTitle && onRenamePlan) {
                      onRenamePlan(trimmed);
                    }
                    setIsEditingName(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.target.blur();
                    }
                    if (e.key === "Escape") {
                      setEditedName(programTitle);
                      setIsEditingName(false);
                    }
                  }}
                  fontWeight="bold"
                  fontSize={{ base: "sm", md: "sm", lg: "lg" }}
                  variant="flushed"
                  size="sm"
                  py={0}
                  height="auto"
                  lineHeight="normal"
                  width="350px"
                  maxWidth="350px"
                  autoFocus
                />
              ) : (
                <HStack
                  spacing={2}
                  cursor={onRenamePlan ? "text" : "default"}
                  w="full"
                  minWidth="0"
                  onMouseEnter={() => setIsHoveringTitle(true)}
                  onMouseLeave={() => setIsHoveringTitle(false)}
                  onClick={() => {
                    if (onRenamePlan) {
                      setEditedName(programTitle);
                      setIsEditingName(true);
                      setTimeout(() => nameInputRef.current?.focus(), 50);
                    }
                  }}
                >
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: "sm", md: "sm", lg: "lg" }}
                    isTruncated
                  >
                    {programTitle}
                  </Text>
                  {onRenamePlan && (
                    <Tooltip label="Rename project" hasArrow placement="top" openDelay={400}>
                      <Icon
                        as={EditIcon}
                        boxSize={4}
                        color="gray.500"
                        opacity={isHoveringTitle ? 1 : 0}
                        transition="opacity 0.15s ease"
                        flexShrink={0}
                      />
                    </Tooltip>
                  )}
                </HStack>
              )}
              {currentPlanId && onStrategyLifecycleStatusChange && !isEditingName && (
                <Menu placement="bottom-start">
                  <Tooltip
                    label={currentStrategyStatus.description}
                    hasArrow
                    placement="bottom"
                    openDelay={400}
                  >
                    <MenuButton
                      as={Button}
                      size="xs"
                      variant="ghost"
                      borderRadius="full"
                      px={2.5}
                      h="24px"
                      fontSize="xs"
                      fontWeight="600"
                      color="gray.700"
                      bg={currentStrategyStatus.bg}
                      flexShrink={0}
                      _hover={{ bg: "gray.100", color: "gray.900" }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Strategy status: ${currentStrategyStatus.label}. ${currentStrategyStatus.description}`}
                    >
                      <HStack spacing={1.5}>
                        <Box boxSize="6px" borderRadius="full" bg={currentStrategyStatus.color} />
                        <Box as="span">{currentStrategyStatus.label}</Box>
                        <ChevronDownIcon boxSize={3.5} color="gray.500" ml={0.5} />
                      </HStack>
                    </MenuButton>
                  </Tooltip>
                  <MenuList minW="240px" py={2} fontSize="sm" zIndex={2000}>
                    <Box px={3} pb={2}>
                      <Text fontSize="xs" fontWeight="700" color="gray.700">
                        Where is this project now?
                      </Text>
                      <Text fontSize="xs" color="gray.500" lineHeight="1.4">
                        Mark the outcome or current state of this idea.
                      </Text>
                    </Box>
                    <Divider my={1} borderColor="gray.100" />
                    {strategyStatusOptions.map(option => (
                      <MenuItem
                        key={option.value}
                        fontSize="sm"
                        fontWeight={option.value === currentStrategyStatus.value ? "600" : "400"}
                        color={option.value === currentStrategyStatus.value ? "gray.900" : "gray.700"}
                        onClick={() => onStrategyLifecycleStatusChange(option.value)}
                      >
                        <HStack w="full" justify="space-between" spacing={3}>
                          <HStack spacing={2} minW="0">
                            <Box boxSize="7px" borderRadius="full" bg={option.color} flexShrink={0} />
                            <Text>{option.label}</Text>
                          </HStack>
                          <Tooltip label={option.description} hasArrow placement="right" openDelay={250}>
                            <Box as="span" className="material-icons-outlined" fontSize="15px" color="gray.400" lineHeight="1" onClick={(e) => e.stopPropagation()}>
                              info
                            </Box>
                          </Tooltip>
                        </HStack>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </HStack>
            <Text
              fontSize="xs"
              color={
                planState === "saved"
                  ? "green.500"
                  : planState === "creating"
                    ? "blue.500"
                    : planState === "loading"
                      ? "blue.500"
                      : planState === "updating"
                        ? "orange.500"
                        : planState === "saving"
                          ? "orange.500"
                          : "green.500"
              }
              opacity={statusVisible ? 1 : 0}
              visibility={shouldShowStatus ? "visible" : "hidden"}
              transition="opacity 0.3s ease-in-out"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {planState === "creating"
                ? "Creating..."
                : planState === "loading"
                  ? "Loading..."
                  : planState === "updating"
                    ? "Updating..."
                    : planState === "saving"
                      ? "Saving..."
                      : planState === "saved"
                        ? "Saved"
                        : "Ready"}
            </Text>
          </HStack>
        )}
      </Flex>
    );
  };

  // Render right-side action buttons
  const renderRightSide = () => {
    return (
      <HStack spacing={{ base: 1, md: 3 }} flexShrink={0} ml="auto">
        {planStatus && planStatus.planType === "free" && (
          <Tooltip
            label="Click to view plan options"
            hasArrow
            placement="bottom"
          >
            <Badge
              display={{ base: "none", md: "inline-flex" }}
              px={3}
              py={1}
              borderRadius="full"
              colorScheme="gray"
              cursor="pointer"
              onClick={() => navigate('/upgrade')}
              textTransform="capitalize"
              fontSize="xs"
              fontWeight="medium"
            >
              Free Plan
            </Badge>
          </Tooltip>
        )}
        
        {isOnPlanPage && currentPlanId && onOpenLearningsPanel && (
          <Tooltip label="See the insights I'm capturing" hasArrow placement="bottom">
            <Box position="relative" display="inline-flex">
              {useCompactPlanActions ? (
                <IconButton
                  aria-label="Open learnings"
                  icon={
                    <Box as="span" className="material-icons-outlined" fontSize="18px">
                      {learningsPendingCount > 0 ? "notifications_active" : "lightbulb"}
                    </Box>
                  }
                  variant={learningsPendingCount > 0 ? "solid" : "ghost"}
                  colorScheme={learningsPendingCount > 0 ? "orange" : "gray"}
                  size="sm"
                  onClick={onOpenLearningsPanel}
                />
              ) : (
                <Button
                  aria-label="Open learnings"
                  leftIcon={
                    <Box as="span" className="material-icons-outlined" fontSize="18px">
                      {learningsPendingCount > 0 ? "notifications_active" : "lightbulb"}
                    </Box>
                  }
                  variant={learningsPendingCount > 0 ? "solid" : "ghost"}
                  colorScheme={learningsPendingCount > 0 ? "orange" : "gray"}
                  size="sm"
                  onClick={onOpenLearningsPanel}
                >
                  Learnings
                </Button>
              )}

              {learningsPendingCount > 0 && (
                <Badge
                  position="absolute"
                  top="-1"
                  right="-1"
                  colorScheme="orange"
                  borderRadius="full"
                  fontSize="9px"
                  minW="16px"
                  h="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  zIndex={1}
                >
                  {learningsPendingCount}
                </Badge>
              )}
            </Box>
          </Tooltip>
        )}
        <Tooltip label="The tasks I'm doing for you" hasArrow placement="bottom">
          <Box position="relative" display="inline-flex" data-walkthrough-anchor="jobs">
            <IconButton
              aria-label="Open jobs"
              icon={<Box as="span" className="material-icons-outlined" fontSize="18px">task_alt</Box>}
              variant={jobsCount > 0 ? "solid" : "ghost"}
              colorScheme={jobsCount > 0 ? "blue" : "gray"}
              size="sm"
              color={jobsCount > 0 ? "white" : "gray.500"}
              _hover={jobsCount > 0 ? { opacity: 0.85 } : { bg: 'gray.100', color: 'gray.700' }}
              onClick={() => setIsJobsOpen(true)}
            />
            {jobsCount > 0 && (
              <Badge
                position="absolute"
                top="-1"
                right="-1"
                colorScheme="blue"
                borderRadius="full"
                fontSize="9px"
                minW="16px"
                h="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
                pointerEvents="none"
              >
                {jobsCount}
              </Badge>
            )}
          </Box>
        </Tooltip>
        {isOnPlanPage && currentPlanId && (onOpenShareModal || onOpenMcpModal) && (
          <Box display="inline-flex">
            <Popover placement="bottom-end" gutter={4}>
              {({ isOpen: menuOpen, onClose: closePopover }) => (
                <>
                  <Tooltip label="Share your plan, connect your sources or connect Pabs to your AI tools" hasArrow placement="bottom" shouldWrapChildren>
                    <PopoverTrigger>
                      {useCompactPlanActions ? (
                        <IconButton
                          icon={<Box as="span" className="material-icons-outlined" fontSize="18px">share</Box>}
                          variant="solid"
                          size="sm"
                          color="white"
                          _hover={{ color: "brand.200", bg: "brand.800" }}
                          aria-label="Share & Connect"
                        />
                      ) : (
                        <Button
                          px={3}
                          variant="solid"
                          size="sm"
                          color="white"
                          _hover={{ color: "brand.200", bg: "brand.800" }}
                          aria-label="Share & Connect"
                        >
                          Share &amp; Connect
                        </Button>
                      )}
                    </PopoverTrigger>
                  </Tooltip>
                  <PopoverContent
                    w="260px"
                    borderRadius="xl"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="gray.100"
                    _focus={{ outline: "none" }}
                    overflow="hidden"
                  >
                  <PopoverBody p={2}>
                    <VStack align="stretch" spacing={0}>
                      {onOpenShareModal && (
                        <>
                          <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="gray.400" px={3} pt={2} pb={1}>
                            Share with others
                          </Text>
                          <Button
                            variant="ghost"
                            size="sm"
                            justifyContent="flex-start"
                            fontWeight="500"
                            fontSize="sm"
                            color="gray.700"
                            borderRadius="lg"
                            leftIcon={<Box as="span" className="material-icons-outlined" fontSize="16px" color="gray.500">share</Box>}
                            _hover={{ bg: "gray.50", color: "gray.900" }}
                            onClick={() => { onOpenShareModal(); closePopover(); }}
                          >
                            Share public link
                          </Button>
                        </>
                      )}
                      <Divider my={1} borderColor="gray.100" />
                      <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="gray.400" px={3} pt={1} pb={1}>
                        Your sources
                      </Text>
                      <Button
                        variant="ghost"
                        size="sm"
                        justifyContent="flex-start"
                        fontWeight="500"
                        fontSize="sm"
                        color="gray.700"
                        borderRadius="lg"
                        leftIcon={<Box as="span" className="material-icons-outlined" fontSize="16px" color="gray.500">cable</Box>}
                        _hover={{ bg: "gray.50", color: "gray.900" }}
                        onClick={() => { navigate('/connectors/knowledge'); closePopover(); }}
                      >
                        Connect your sources to Pabs
                      </Button>
                      {onOpenMcpModal && (
                        <>
                          <Divider my={1} borderColor="gray.100" />
                          <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="gray.400" px={3} pt={2} pb={1}>
                            Your AI tools
                          </Text>
                          <Button
                            variant="ghost"
                            size="sm"
                            justifyContent="flex-start"
                            fontWeight="500"
                            fontSize="sm"
                            color="gray.700"
                            borderRadius="lg"
                            leftIcon={<Box as="span" className="material-icons-outlined" fontSize="16px" color="gray.500">terminal</Box>}
                            _hover={{ bg: "gray.50", color: "gray.900" }}
                            onClick={() => { onOpenMcpModal(); closePopover(); }}
                          >
                            Connect Pabs to your AI tools
                          </Button>
                          {menuOpen && <ConnectedToolsSummary planId={currentPlanId} />}
                        </>
                      )}
                    </VStack>
                  </PopoverBody>
                  </PopoverContent>
                </>
              )}
            </Popover>
          </Box>
        )}
        <UserMenu />
      </HStack>
    );
  };

  return (
    <Box bg="transparent">
      <Flex
        ref={navRef}
        as="nav"
        justify="space-between"
        pl={0}
        pr={4}
        pt={2}
        width="100%"
        bg="transparent"
        alignItems="center"
      >
        {renderLeftSide()}
        {renderRightSide()}
        {!isOnPlanPage && (
          <FloatingChatbot
            ref={chatbotRef}
            prefilledPrompt={null}
            triggeredByButton={triggeredByButton}
            currentDesignChallenge={currentDesignChallenge}
            selectedPrograms={selectedPrograms}
            shortSummary={shortSummary}
            goalStatement={goalStatement}
            handleComponentAction={handleComponentAction}
            targetStatement={targetStatement}
            objectivesStatement={objectivesStatement}
            deliverablesStatement={deliverablesStatement}
            metricsStatement={metricsStatement}
            stakeholdersCustomersInvolvement={stakeholdersCustomersInvolvement}
            finalProgramSequence={finalProgramSequence}
            totalDuration={totalDuration}
            planStatus={planStatus}
          />
        )}
      </Flex>

      <JobsDrawer isOpen={isJobsOpen} onClose={() => { setIsJobsOpen(false); setJobsInitialTaskId(null); }} defaultTab={jobsDefaultTab} currentPlanId={currentPlanId} initialTaskId={jobsInitialTaskId} hideProjectContext={isOnPlanPage} />

      {/* SOW Modal */}
      <SOWModal
        isOpen={isSOWModalOpen}
        onClose={handleCloseSOWModal}
        planData={{
          currentDesignChallenge,
          shortSummary,
          problemStatement,
          goalStatement,
          targetStatement,
          deliverablesStatement,
          objectivesStatement,
          metricsStatement,
          stakeholdersCustomersInvolvement,
          selectedPrograms,
          finalProgramSequence,
          totalDuration,
        }}
      />

    </Box>
  );
}

export default NavBar;
